/**
 * AccountTypeSelectionModal.jsx — Refonte lumineuse premium (Iter309)
 * ====================================================================
 *
 * Modale de sélection du type de compte : Individuel ou Entreprise
 * Refonte du thème sombre (slate-900) → ivoire/blanc cohérent Iter304-308.
 *
 * Palette : #FAFAF8 fond, #FFFFFF cartes, #1C1147 ink, #D4AF37 or, #7A4FCF violet.
 *
 * RÈGLES MOA P1 (23 Jan 2026):
 * - Affichée OBLIGATOIREMENT avant la modale des packs
 * - Deux choix : Individuel (packs) ou Entreprise (B2B)
 * - Bloque l'accès direct aux packs sans contexte billing
 */

import React from 'react';
import {
  X,
  User,
  Building2,
  Check,
  Sparkles,
  ArrowRight,
  CreditCard,
  Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const T = {
  ink: '#1C1147',
  inkSoft: '#5A5169',
  ivory: '#FAFAF8',
  border: '#E8E4D9',
  goldDeep: '#B7901F',
  gold: '#D4AF37',
  goldLight: '#F3D060',
  violet: '#7A4FCF',
  violetSoft: '#A78BFA',
};

export function AccountTypeSelectionModal({
  isOpen,
  onClose,
  onSelectIndividual,
  onSelectEnterprise,
}) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {/* Iter315 — Outer scrollable container : 100dvh + overflow-y-auto pour
          permettre le scroll fluide sur mobile (URL bar dynamique iOS/Android),
          jamais bloqué. p-0 sur l'outer, padding géré par l'inner wrapper avec
          safe-area-insets pour ne jamais cacher le bouton X. */}
      <div
        className="fixed inset-0 z-[10003] overflow-y-auto overscroll-contain"
        style={{
          background: 'rgba(28,17,71,0.42)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
        data-testid="account-type-selection-backdrop"
      >
        <div
          className="min-h-[100dvh] flex items-start sm:items-center justify-center"
          style={{
            paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right))',
          }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 24 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl rounded-[24px] overflow-hidden my-auto"
            style={{
              background: T.ivory,
              border: `1px solid ${T.border}`,
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.7) inset, 0 30px 80px -28px rgba(28,17,71,0.45), 0 0 0 1px rgba(212,175,55,0.10)',
            }}
            data-testid="account-type-selection-modal"
          >
          {/* halos ambient */}
          <span
            aria-hidden
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)',
              filter: 'blur(28px)',
            }}
          />
          <span
            aria-hidden
            className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(122,79,207,0.10) 0%, transparent 70%)',
              filter: 'blur(28px)',
            }}
          />

          {/* Iter315 — Close Button : zone de tap ≥44px (Apple HIG), padding
              tactile généreux, position absolute avec safe-area-inset géré par
              le wrapper extérieur. Toujours visible et cliquable. */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 rounded-full transition-all z-20 touch-manipulation flex items-center justify-center"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              color: T.ink,
              width: '44px',
              height: '44px',
              boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 12px -6px rgba(28,17,71,0.18)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212,175,55,0.55)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = T.border;
            }}
            data-testid="close-type-selection-modal"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header — Iter315 : padding-top accru pour ne jamais chevaucher le X (44px+marge) */}
          <div className="px-6 sm:px-10 pt-16 sm:pt-12 pb-3 text-center relative">
            <div className="flex items-center justify-center gap-2 mb-3">
              <span
                className="w-6 h-px"
                style={{
                  background: `linear-gradient(90deg, transparent, ${T.gold})`,
                }}
              />
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.22em]"
                style={{ color: T.goldDeep }}
              >
                Souscription
              </span>
              <span
                className="w-6 h-px"
                style={{
                  background: `linear-gradient(90deg, ${T.gold}, transparent)`,
                }}
              />
            </div>
            <h2
              className="font-serif mb-2"
              style={{
                fontSize: 'clamp(1.45rem, 3vw, 2rem)',
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                fontWeight: 600,
                color: T.ink,
              }}
            >
              Comment souhaitez-vous accéder à{' '}
              <span
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Coach DIA
              </span>
              {' '}?
            </h2>
            <p
              className="text-[14px] sm:text-[15px]"
              style={{ color: T.inkSoft }}
            >
              Choisissez le type de souscription adapté à vos besoins.
            </p>
          </div>

          {/* Cards Container */}
          <div className="px-6 sm:px-10 py-6 grid sm:grid-cols-2 gap-4 sm:gap-5 relative">
            {/* ─── Card Individuel ─── */}
            <motion.button
              type="button"
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.985 }}
              onClick={onSelectIndividual}
              className="text-left rounded-2xl p-5 sm:p-6 transition-all group relative overflow-hidden"
              style={{
                background: '#FFFFFF',
                border: `1px solid ${T.border}`,
                boxShadow:
                  '0 1px 0 rgba(28,17,71,0.04), 0 14px 30px -22px rgba(28,17,71,0.20)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212,175,55,0.55)';
                e.currentTarget.style.boxShadow =
                  '0 1px 0 rgba(212,175,55,0.20), 0 22px 50px -24px rgba(28,17,71,0.30), 0 0 0 1px rgba(212,175,55,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.boxShadow =
                  '0 1px 0 rgba(28,17,71,0.04), 0 14px 30px -22px rgba(28,17,71,0.20)';
              }}
              data-testid="select-individual-btn"
            >
              {/* halo hover or */}
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(120% 80% at 0% 0%, rgba(212,175,55,0.10) 0%, transparent 55%)',
                  transition: 'opacity 320ms ease',
                }}
              />

              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 relative"
                style={{
                  background: `linear-gradient(135deg, ${T.goldDeep} 0%, ${T.gold} 50%, ${T.goldLight} 100%)`,
                  boxShadow:
                    '0 10px 24px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
              >
                <User className="w-6 h-6 text-white" />
              </div>

              {/* Title */}
              <div className="flex items-center justify-between mb-1 relative">
                <h3
                  className="font-serif"
                  style={{
                    color: T.ink,
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Individuel
                </h3>
                <ArrowRight
                  className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  style={{ color: T.goldDeep }}
                />
              </div>

              {/* Subtitle */}
              <p
                className="text-sm font-semibold mb-4"
                style={{ color: T.goldDeep }}
              >
                À partir de 5,90&nbsp;€/mois
              </p>

              {/* Features */}
              <ul className="space-y-2 text-sm relative">
                <li className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(72,130,90,0.12)' }}
                  >
                    <Check className="w-2.5 h-2.5" style={{ color: '#48825A' }} />
                  </span>
                  <span style={{ color: T.ink }}>30, 73 ou 150 sessions/mois</span>
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(72,130,90,0.12)' }}
                  >
                    <Check className="w-2.5 h-2.5" style={{ color: '#48825A' }} />
                  </span>
                  <span style={{ color: T.ink }}>Sans engagement</span>
                </li>
              </ul>

              {/* CTA hint */}
              <div
                className="mt-5 pt-4 flex items-center gap-1.5"
                style={{ borderTop: `1px solid ${T.border}` }}
              >
                <Sparkles className="w-4 h-4" style={{ color: T.goldDeep }} />
                <span
                  className="text-sm font-semibold"
                  style={{ color: T.goldDeep }}
                >
                  Choisir un pack
                </span>
              </div>
            </motion.button>

            {/* ─── Card Entreprise ─── */}
            <motion.button
              type="button"
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.985 }}
              onClick={onSelectEnterprise}
              className="text-left rounded-2xl p-5 sm:p-6 transition-all relative overflow-hidden group"
              style={{
                background: '#FFFFFF',
                border: `1px solid ${T.border}`,
                boxShadow:
                  '0 1px 0 rgba(28,17,71,0.04), 0 14px 30px -22px rgba(28,17,71,0.20)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(122,79,207,0.50)';
                e.currentTarget.style.boxShadow =
                  '0 1px 0 rgba(122,79,207,0.18), 0 22px 50px -24px rgba(28,17,71,0.30), 0 0 0 1px rgba(122,79,207,0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = T.border;
                e.currentTarget.style.boxShadow =
                  '0 1px 0 rgba(28,17,71,0.04), 0 14px 30px -22px rgba(28,17,71,0.20)';
              }}
              data-testid="select-enterprise-btn"
            >
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(120% 80% at 100% 0%, rgba(122,79,207,0.10) 0%, transparent 55%)',
                  transition: 'opacity 320ms ease',
                }}
              />

              {/* Badge ÉQUIPES */}
              <span
                className="absolute top-4 right-4 text-[10px] font-bold tracking-[0.14em] px-2 py-0.5 rounded-full"
                style={{
                  background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
                  color: '#FFFFFF',
                  boxShadow: '0 6px 14px -6px rgba(122,79,207,0.55)',
                }}
              >
                ÉQUIPES
              </span>

              {/* Icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 relative"
                style={{
                  background: `linear-gradient(135deg, ${T.violet}, ${T.violetSoft})`,
                  boxShadow:
                    '0 10px 24px -10px rgba(122,79,207,0.55), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <Building2 className="w-6 h-6 text-white" />
              </div>

              {/* Title */}
              <div className="flex items-center justify-between mb-1 relative">
                <h3
                  className="font-serif"
                  style={{
                    color: T.ink,
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Entreprise
                </h3>
                <ArrowRight
                  className="w-4 h-4 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all"
                  style={{ color: T.violet }}
                />
              </div>

              {/* Subtitle */}
              <p
                className="text-sm font-semibold mb-4"
                style={{ color: T.violet }}
              >
                Licences flexibles · Prix sur mesure
              </p>

              {/* Features */}
              <ul className="space-y-2 text-sm relative">
                <li className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(72,130,90,0.12)' }}
                  >
                    <Check className="w-2.5 h-2.5" style={{ color: '#48825A' }} />
                  </span>
                  <span style={{ color: T.ink }}>Gestion centralisée</span>
                </li>
                <li className="flex items-center gap-2">
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(72,130,90,0.12)' }}
                  >
                    <Check className="w-2.5 h-2.5" style={{ color: '#48825A' }} />
                  </span>
                  <span style={{ color: T.ink }}>Facturation entreprise</span>
                </li>
              </ul>

              {/* CTA hint */}
              <div
                className="mt-5 pt-4 flex items-center gap-1.5"
                style={{ borderTop: `1px solid ${T.border}` }}
              >
                <Building2 className="w-4 h-4" style={{ color: T.violet }} />
                <span
                  className="text-sm font-semibold"
                  style={{ color: T.violet }}
                >
                  Configurer mon équipe
                </span>
              </div>
            </motion.button>
          </div>

          {/* Footer Sécurité */}
          <div
            className="px-6 sm:px-10 pb-7 pt-3 text-center relative"
          >
            <div
              className="inline-flex items-center gap-3 sm:gap-4 px-4 py-2 rounded-full text-[12px]"
              style={{
                background: 'rgba(255,255,255,0.7)',
                border: `1px solid ${T.border}`,
                color: T.inkSoft,
                backdropFilter: 'blur(8px)',
              }}
            >
              <span className="inline-flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5" style={{ color: T.goldDeep }} />
                Paiement 100% sécurisé par Stripe
              </span>
              <span style={{ color: T.border }}>·</span>
              <span className="inline-flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" style={{ color: T.goldDeep }} />
                Données cryptées
              </span>
            </div>
          </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}

export default AccountTypeSelectionModal;
